import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { createMachine } from "xstate";
import { auth } from "@/services/auth";
import { userStore } from "@/stores/userStore";
import { toast } from "react-toastify";
import { FirebaseError } from "firebase/app";
import { apiService } from "@/services/apiService";
import type { User } from "@repo/types";
import { config } from "@/config";
import * as Sentry from "@sentry/react";
import { messageService } from "@/services/messageService/clientMessageService";
import { accountRepo } from "@/repositories/accountRepo";
import { firstValueFrom } from "rxjs";
import { userRepo } from "@/repositories/userRepo";
import { baseRepo } from "@/repositories/baseRepo";

export const authMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAYgHEB5AfQBVqBBAVRoAkBRAORoEkBhemmwAiAbQAMAXUSgADgHtYuAC645+aSAAeiACwAmADQgAnroCMAZgB0AVgC+do2ix5CpSrWqMOTVpx78gqKSGvKKKmoa2gj6RqYIemIAHLYOThg4BMRW6ACuSthg+CqY6EqQJADK3GQcFMziUkggYcqq6s3RFmYpNgCcFgDsNnGINmJ9aSDOmW45+YXFuKXlEFaKUPgEUBT5JBBqYOtKZUczrtl5BUUlp2sbW-g7+Y2hCm2RnYgAbHp9Vt9Bj1hqMEDYzGJUo5phkLkQrLl8FdFrdVlUahxuBxXs1WhEOqAujpIWY9CMTIg9HozFNzll4YjkTdlnd1rhNttuPh9odjqcrHS5oyFsyVpA2RynlycbJ3vioj8qVZBklSeT4lS9FDofg5BA4BpBcQ3uF2gqEABab6gi16HS02H0qwAGzk6Ag2xNHwJWl0hgpCDM4wdLidTKWYogXvlXwSg0h-SG6sQFj6vRDs0uIojrNwEGdYGjZtjgx0OisFh0SdB43+9mhRvh4dR4oe212SiLn0JunGVn0wOTMSS3wzcIRSOzLajuLlxZ7CT6WrVoLMEKh6VDQsn1xzqyseYLXZ90UGQ37Nm6ZNBVLMG5hW+ywt304ljygXOP5p0NnLgMHq5pg4DhAA */
    tsTypes: {} as import("./authMachine.typegen").Typegen0,
    predictableActionArguments: true,
    schema: {
      context: {},
      events: {} as
        | {
            type: "SIGNIN";
            data: {
              email: string;
              password: string;
              setSubmitting: (arg: boolean) => void;
            };
          }
        | {
            type: "SIGNUP";
            data: {
              email: string;
              password: string;
              firstName: string;
              lastName: string;
              next?: (success: boolean) => void;
            };
          }
        | { type: "SENDVERIFICATIONEMAIL" }
        | { type: "SIGNOUT" }
        | { type: "RESETPASSWORD"; data: { email: string } }
        | { type: "GO_TO_AUTHENTICATED" }
        | { type: "GO_TO_UNVERIFIED" }
        | { type: "GO_TO_UNAUTHENTICATED" }
        | { type: "GO_TO_UNAUTHORIZED" }
        | { type: "RELOADUSER" },
      services: {} as {
        userSubscriber: { data: void };
        signIn: { data: void };
        signOut: { data: void };
        signUp: { data: void };
        reloadUser: { data: void };
        sendEmailVerification: { data: void };
        resetPassword: { data: void };
      },
    },

    invoke: { src: "userSubscriber" },
    on: {
      GO_TO_AUTHENTICATED: { target: "authenticated", internal: true },
      GO_TO_UNAUTHENTICATED: { target: "unauthenticated", internal: true },
      GO_TO_UNAUTHORIZED: { target: "unauthorized", internal: true },
      GO_TO_UNVERIFIED: { target: "unverified", internal: true },
    },
    initial: "loading",
    states: {
      loading: {
        tags: "loading",
      },
      authenticated: {
        on: { SIGNOUT: { target: ".signingOut" } },
        initial: "idle",
        states: {
          idle: {},

          signingOut: {
            invoke: { src: "signOut", onDone: "idle" },
            onDone: { target: "idle" },
          },
        },
      },
      unverified: {
        on: {
          SENDVERIFICATIONEMAIL: { target: ".verifyEmail" },
          RELOADUSER: { target: ".reloadingUser" },
        },
        initial: "idle",
        states: {
          idle: {},
          verifyEmail: {
            invoke: { src: "sendEmailVerification", onDone: "idle" },
            onDone: { target: "idle" },
          },
          reloadingUser: {
            invoke: { src: "reloadUser", onDone: "idle" },
            onDone: { target: "idle" },
          },
        },
      },
      unauthorized: {
        on: { SIGNOUT: { target: ".signingOut" } },
        initial: "idle",
        states: {
          idle: {},

          signingOut: {
            invoke: { src: "signOut", onDone: "idle" },
            onDone: { target: "idle" },
          },
        },
      },
      unauthenticated: {
        on: {
          SIGNIN: { target: ".signingIn" },
          SIGNUP: { target: ".signingUp" },
          RESETPASSWORD: { target: ".resettingPassword" },
        },
        initial: "idle",
        states: {
          idle: {},
          signingIn: {
            invoke: { src: "signIn", onDone: "idle" },
            onDone: { target: "idle" },
          },
          signingUp: {
            invoke: { src: "signUp", onDone: "idle" },
            onDone: { target: "idle" },
          },
          resettingPassword: {
            invoke: { src: "resetPassword", onDone: "idle" },
            onDone: { target: "idle" },
          },
        },
      },
    },
  },
  {
    services: {
      userSubscriber: () => {
        return (sendBack) => {
          const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            if (authUser && authUser.emailVerified && authUser.email) {
              const lastLoginTime = new Date();
              let accountData = await firstValueFrom(
                accountRepo.getMyAccount()
              );

              // try to find current user's User object
              let userData = await firstValueFrom(
                userRepo.getByEmail(authUser.email.toLowerCase())
              );

              // get current user's Account object, or create one it if it doesn't exist
              if (accountData) {
                accountData = await accountRepo.updateSelf({
                  id: authUser.uid,
                  emailVerified: authUser.emailVerified,
                  lastLoginTime,
                });
              } else {
                if (!userData) return; // there's nothing we can do. onAuthStateChanged was triggered way too early to know anything.

                accountData = await accountRepo.createSelf({
                  id: authUser.uid,
                  email: authUser.email.toLowerCase(),
                  emailVerified: authUser.emailVerified,
                  firstName: userData.firstName,
                  lastName: userData.lastName,
                  lastLoginTime,
                });
              }

              // assert that accountData exists
              if (!accountData) {
                console.warn(
                  "Assertion Failed: No account, even after creation or update."
                );
                return;
              }

              // update account in userStore
              userStore.getState().setAccount(accountData);

              // update or create user object
              if (userData) {
                // user object was found with email: pull in stripe data

                const updateUser: Partial<User> = {
                  firstName: userData.firstName || accountData?.firstName || "",
                  lastName: userData.lastName || accountData?.lastName || "",
                  lastLoginTime,
                  emailVerified: authUser.emailVerified,
                };

                // if no subscription details found, pull from stripe
                if (!userData.subscriptionId) {
                  console.log("Getting subscription");
                  let stripeCustomerId;
                  try {
                    stripeCustomerId = (
                      await apiService.v1.getCustomersByEmail({
                        email: userData.email,
                      })
                    ).data;
                  } catch (err) {
                    // noop
                  }

                  if (stripeCustomerId) {
                    console.log(stripeCustomerId);
                    if (stripeCustomerId.stripeId) {
                      console.log("Adding in subscriber");
                      updateUser.stripeCustomerId = stripeCustomerId.stripeId;
                    }

                    if (stripeCustomerId.subscriptions?.length) {
                      updateUser.subscriptionId =
                        stripeCustomerId.subscriptions[0].id ?? "";
                      updateUser.subscriptionStatus =
                        stripeCustomerId.subscriptions[0].status ?? "";
                    }
                  }
                }

                console.log("updating user", updateUser);
                userData = await userRepo.update(userData.id, updateUser);

                if (!userData) {
                  throw Error(
                    "User Data did not return even though we just wrote it"
                  );
                }
              } else {
                // user object doesn't exist yet: create one
                userData = await userRepo.createSelf(accountData);

                if (!userData) {
                  throw Error(
                    "User Data did not return even though we just wrote it"
                  );
                }
              }

              userStore.setState({ id: userData.id }); // set id to user's id
              Sentry.setUser({ id: userData.id, email: userData.email });
              await userStore.getState().setUser(userData);

              sendBack({ type: "GO_TO_AUTHENTICATED" });
            } else if (authUser) {
              sendBack({ type: "GO_TO_UNVERIFIED" });
            } else {
              userStore.getState().resetUser();
              Sentry.setUser(null);
              sendBack({ type: "GO_TO_UNAUTHENTICATED" });
            }
            apiService.setSecurityData();
            messageService.connect();
          });
          return () => unsubscribe();
        };
      },
      signIn: async (_, { data }) => {
        await signInWithEmailAndPassword(auth, data.email, data.password)
          .catch((e: FirebaseError) => {
            console.log(e);
            toast.error(e.message);
          })
          .finally(() => data.setSubmitting(false));
      },
      signOut: async () => {
        await auth.signOut();
        baseRepo.clearAllCache();
      },
      signUp: async (_, { data }) => {
        await createUserWithEmailAndPassword(auth, data.email, data.password)
          .then((res) => {
            console.log(res);
            sendEmailVerification(res.user);
            return accountRepo.createSelf({
              id: res.user.uid,
              email: data.email,
              emailVerified: res.user.emailVerified,
              firstName: data.firstName,
              lastName: data.lastName,
              lastLoginTime: new Date(),
            });
          })
          .then(() => {
            data.next?.(true);
          })
          .catch((error) => {
            switch (error.code) {
              case "auth/email-already-in-use":
                toast(
                  "That email is already used. Please login, or sign up with a different email.",
                  { type: "error" }
                );
                break;
              default:
                toast("An error occurred signing up: " + error.code, {
                  type: "error",
                });
                break;
            }
            data.next?.(false);
          });
      },
      reloadUser: () => async (sendBack) => {
        if (auth.currentUser) {
          await auth.currentUser.reload();
          if (auth.currentUser.emailVerified) {
            sendBack({ type: "GO_TO_AUTHENTICATED" });
          }
          console.log("reloadUser");
        }
      },
      sendEmailVerification: async () => {
        if (auth.currentUser) {
          await sendEmailVerification(auth.currentUser, {
            url: `${config.urls.admin}/verify`,
          }).catch((e: FirebaseError) => {
            console.log(e);
            toast.error(e.message);
          });
        }
      },
      resetPassword: async (_, { data }) => {
        await sendPasswordResetEmail(auth, data.email);
      },
    },
  }
);
